import { createClient, type SupabaseClient, type User, type AuthChangeEvent, type Session } from '@supabase/supabase-js'

let _client: SupabaseClient | null = null

/** Returns a shared Supabase client, or null if env vars are not set. */
export function getSupabase(): SupabaseClient | null {
  if (_client) return _client
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined
  if (url && key) {
    _client = createClient(url, key)
  }
  return _client
}

export const isSupabaseConfigured = (): boolean =>
  Boolean(
    import.meta.env.VITE_SUPABASE_URL &&
    import.meta.env.VITE_SUPABASE_ANON_KEY
  )

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

export interface AuthResult {
  error: string | null
  needsEmailVerification?: boolean
}

export interface ConsentDetails {
  agreedTerms: boolean
  agreedParent: boolean
  version: string
}

/** Sign up with email + password. Records consent at signup time. */
export async function authSignUp(
  email: string,
  password: string,
  consent: ConsentDetails,
): Promise<AuthResult> {
  const supabase = getSupabase()
  if (!supabase) return { error: 'Supabase is not configured.' }
  const { error, data } = await supabase.auth.signUp({
    email,
    password,
    options: { emailRedirectTo: window.location.origin },
  })
  if (error) return { error: error.message }

  // Record consent immediately — user exists even if email verification is pending
  if (data.user) {
    await supabase.from('consent_records').insert({
      user_id: data.user.id,
      version: consent.version,
      consents: {
        agreedTerms: consent.agreedTerms,
        agreedParent: consent.agreedParent,
        consentVersion: consent.version,
      },
    })
    // Silently ignore insert errors — don't block signup if audit write fails
  }

  const needsEmailVerification = !data.session
  return { error: null, needsEmailVerification }
}

/** Sign in with email + password. Returns error string or null on success. */
export async function authSignIn(email: string, password: string): Promise<AuthResult> {
  const supabase = getSupabase()
  if (!supabase) return { error: 'Supabase is not configured.' }
  const { error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) return { error: error.message }
  return { error: null }
}

/** Sign out the current user. */
export async function authSignOut(): Promise<void> {
  const supabase = getSupabase()
  if (!supabase) return
  await supabase.auth.signOut()
}

/** Get the current session and user (null if not logged in). */
export async function getAuthSession(): Promise<{ user: User | null }> {
  const supabase = getSupabase()
  if (!supabase) return { user: null }
  const { data } = await supabase.auth.getSession()
  return { user: data.session?.user ?? null }
}

/** Subscribe to auth state changes. Returns an unsubscribe function. */
export function onAuthStateChange(
  callback: (event: AuthChangeEvent, session: Session | null) => void
): () => void {
  const supabase = getSupabase()
  if (!supabase) return () => {}
  const { data } = supabase.auth.onAuthStateChange(callback)
  return () => data.subscription.unsubscribe()
}

/** Update the current user's password (called after PASSWORD_RECOVERY event). */
export async function updatePassword(newPassword: string): Promise<AuthResult> {
  const supabase = getSupabase()
  if (!supabase) return { error: 'Supabase is not configured.' }
  const { error } = await supabase.auth.updateUser({ password: newPassword })
  if (error) return { error: error.message }
  return { error: null }
}

/** Send a password reset email. */
export async function resetPassword(email: string): Promise<AuthResult> {
  const supabase = getSupabase()
  if (!supabase) return { error: 'Supabase is not configured.' }
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${import.meta.env.VITE_APP_URL ?? window.location.origin}?reset=true`,
  })
  if (error) return { error: error.message }
  return { error: null }
}

/** Resend confirmation email for an address. */
export async function resendConfirmation(email: string): Promise<AuthResult> {
  const supabase = getSupabase()
  if (!supabase) return { error: 'Supabase is not configured.' }
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: { emailRedirectTo: window.location.origin },
  })
  if (error) return { error: error.message }
  return { error: null }
}
